import { ogImageMeta } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { TalkToJonButton } from "@/components/talk-to-jon";
import { SiteFooter } from "@/components/site-footer";

const TITLE = "Hire a Full-Stack Designer — Design and Ship | Vibing Company";
const DESC =
  "A full-stack designer designs the interface and builds the stack underneath it. Here's what that means, when to hire one, and how we work.";

const FAQS = [
  {
    q: "What is a full-stack designer?",
    a: "A UI/UX designer who also builds the thing: interface, front-end, database, auth and deployment. One person holds the whole picture, so the design that gets shipped is the design that was drawn.",
  },
  {
    q: "How is that different from a designer who codes?",
    a: "A designer who codes can usually build the screens. A full-stack designer also owns the data model, the permissions and the deploy — the parts that decide whether the product actually works for a second user.",
  },
  {
    q: "Why does this work now?",
    a: "AI build tools collapsed the cost of the engineering half. A designer with judgement and an AI stack can ship what used to need a small team — as long as someone senior still reviews the security and the data.",
  },
  {
    q: "When should I hire one instead of an agency team?",
    a: "When the project is small enough that handoffs cost more than they add: early products, redesigns, internal tools, anything where speed and taste matter more than headcount.",
  },
];

const BLOCKS = [
  {
    h: "One person, no handoff",
    p: "The usual loss happens between the design file and the build. When the same person does both, the spacing survives, the states exist, and nothing gets 'approximated' during implementation.",
  },
  {
    h: "Design decisions with the stack in view",
    p: "Knowing what the database will do changes what you draw. Screens get designed around the data that actually exists, so the product is quicker to build and less prone to the half-empty state nobody planned for.",
  },
  {
    h: "Shipped, not delivered as files",
    p: "The output is a live product on your accounts — real React and TypeScript, deployed, with auth and a database — not a folder of mockups and a quote for the build.",
  },
  {
    h: "And the unglamorous half",
    p: "Row-level security, validation, accessibility, performance. The parts AI tools get wrong by default, checked before launch rather than after an incident.",
  },
];

export const Route = createFileRoute("/full-stack-designer")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: "Hire a Full-Stack Designer — Design and Ship" },
      ...ogImageMeta("home", "Share card: full-stack designer"),
      { property: "og:description", content: DESC },
      { property: "og:url", content: "https://vibing.inc/full-stack-designer" },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: "https://vibing.inc/full-stack-designer" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          name: "the Vibing Company",
          description: DESC,
          url: "https://vibing.inc/full-stack-designer",
          areaServed: "Worldwide",
          serviceType: "Full-stack design",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <main id="main" tabIndex={-1}>
        <section
          aria-labelledby="f-h"
          className="relative mx-auto max-w-[1100px] px-6 md:px-10 pt-[clamp(9rem,18vh,13rem)] pb-12"
        >
          <p className="text-sm uppercase tracking-wider text-muted-foreground">Full-stack design</p>
          <h1
            id="f-h"
            className="mt-6 font-display tracking-[-0.035em] leading-[1.02]"
            style={{ fontSize: "clamp(2rem, 4.4vw, 3.5rem)" }}
          >
            Designed and built by the same hands.
          </h1>
          <p className="mt-10 max-w-[60ch] text-xl md:text-2xl leading-snug text-foreground">
            A full-stack designer draws the interface and builds the stack beneath it — so the thing you approved is the thing that ships.
          </p>
          <div className="mt-10">
            <TalkToJonButton variant="dark" immersive />
          </div>
        </section>

        <article className="mx-auto max-w-[1100px] px-6 md:px-10 py-12 md:py-20 space-y-16 md:space-y-24">
          {BLOCKS.map((b) => (
            <section
              key={b.h}
              className="grid md:grid-cols-12 gap-8 md:gap-10 border-t border-border/60 pt-12 md:pt-16"
            >
              <div className="md:col-span-4">
                <h2
                  className="mt-4 font-display tracking-[-0.03em] leading-[1.02]"
                  style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}
                >
                  {b.h}
                </h2>
              </div>
              <div className="md:col-span-8 text-base md:text-lg leading-relaxed text-foreground/90 max-w-[64ch]">
                <p>{b.p}</p>
              </div>
            </section>
          ))}
        </article>

        <section
          aria-labelledby="f-faq"
          className="relative border-t border-border/60 mx-auto max-w-[1100px] px-6 md:px-10 py-20 md:py-32"
        >
          <h2 id="f-faq" className="fluid-h2 font-display max-w-[18ch]">
            Full-stack design, answered.
          </h2>
          <dl className="mt-14 border-t border-border">
            {FAQS.map((f) => (
              <div key={f.q} className="grid md:grid-cols-12 gap-6 py-8 border-b border-border">
                <dt className="md:col-span-5 font-display text-xl md:text-2xl tracking-[-0.02em] leading-snug">
                  {f.q}
                </dt>
                <dd className="md:col-span-6 md:col-start-7 text-base md:text-lg text-muted-foreground leading-relaxed">
                  {f.a}
                </dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="relative border-t border-border/60 mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28 text-center">
          <h2 className="fluid-h2 font-display max-w-[22ch] mx-auto">
            Got something to design and ship?
          </h2>
          <div className="mt-10 flex justify-center">
            <TalkToJonButton variant="dark" immersive />
          </div>
          <div className="mt-10 flex flex-wrap justify-center gap-6">
            <Link
              to="/vibe-coding-agency"
              className="font-display text-electric underline underline-offset-4"
            >
              → Vibe coding agency
            </Link>
            <Link to="/services" className="font-display text-electric underline underline-offset-4">
              → What we do
            </Link>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
